/**
 * useMapInteraction.js
 * Handles map clicks, shape selection, modes, and cursor management
 */

import { ref, computed, watch, toRef } from 'vue'
import { get } from 'lodash'
import { useMainStore } from '@/stores/main'
import { useDialogsStore } from '@/stores/dialogs'
import { NEW_AREA, snackbarDefaults } from '../../../constants'
import ControlOverlay from '../../ControlOverlay'
import ClickableBehavior from '../../behaviors/ClickableBehavior'

const cursorDraw = 'crosshair'
const cursorNormal = 'pointer'

export function useMapInteraction(map, shapeRegistry, currentInitedShape, buefy) {
  const mainStore = useMainStore()
  const dialogsStore = useDialogsStore()
  const shapeSelectOverlay = ref(null)
  const cursor = ref(null)

  const mode = toRef(mainStore, 'mode')
  const toolType = toRef(mainStore, 'toolType')
  const currentControlOverlay = toRef(mainStore, 'currentControlOverlay')

  /**
   * Set cursor based on current mode
   */
  const setCursor = () => {
    let newCursor
    switch (mode.value) {
      case 'move':
        newCursor = cursorNormal
        break

      case 'draw':
      case 'free':
        newCursor = cursorDraw
        break
    }
    cursor.value = newCursor
  }

  /**
   * Show/hide shape selection overlay when overlapping shapes are clicked
   * @param {Object} event - Map click event
   * @param {boolean} visible - Show or hide overlay
   * @param {Object} shapePayload - { area, distance }
   */
  const setShapeSelectOverlayVisibility = (event, visible = true, shapePayload) => {
    if (!map.value) return

    if (!shapeSelectOverlay.value) {
      shapeSelectOverlay.value = new ControlOverlay(map.value, {
        options: {
          distance: 30,
          prefix: 'shapeselect',
          iconsOnly: false
        },
        buttons: [{
          icon: 'vector-polygon-icon',
          text: 'Select area',
          title: 'Select area measurement',
          name: 'select-area',
          behavior: new ClickableBehavior(),
          tooltipPlacement: 'top'
        }, {
          icon: 'arrow-expand-icon',
          text: 'Select distance',
          title: 'Select distance measurement',
          name: 'select-distance',
          behavior: new ClickableBehavior(),
          tooltipPlacement: 'bottom'
        }]
      })
    }

    shapeSelectOverlay.value.dispatcher.off('select-area-click')
    shapeSelectOverlay.value.dispatcher.off('select-distance-click')
    shapeSelectOverlay.value.dispatcher.on('select-area-click', () => {
      mainStore.setLastMapClickEvent({ event, delayedClear: true })
      mainStore.setCurrentShape(shapePayload.area)
    })
    shapeSelectOverlay.value.dispatcher.on('select-distance-click', () => {
      mainStore.setLastMapClickEvent({ event, delayedClear: true })
      mainStore.setCurrentShape(shapePayload.distance)
    })

    if (visible && event.latLng) {
      shapeSelectOverlay.value.setPosition(event.latLng)
      dialogsStore.snackbar(0, 'Select area or distance measurement')
    }

    shapeSelectOverlay.value.setVisible(visible)
  }

  /**
   * Handle map click events based on current mode
   * @param {Object} event - Google Maps click event
   */
  const handleMapClick = (event) => {
    if (!map.value || map.value.get('_noclicks')) {
      return
    }

    // Free draw mode: create new shape at click location
    if (mode.value == 'free') {
      mainStore.setLastMapClickEvent({ event, delayedClear: true })

      mainStore.addShape(new NEW_AREA({
        type: toolType.value,
        shape: 'free',
        paths: [mainStore.lastMapClickLatLng]
      }))
      mainStore.setMode('draw')

      return
    }

    // Draw mode: add vertex to current shape
    if (mode.value == 'draw') {
      if (currentInitedShape.value) {
        currentInitedShape.value.addCoordinate(event.latLng)
      }
      return
    }

    // Move mode: select shape or handle overlapping shapes
    if (mode.value == 'move') {
      // Close shape selector if open
      if (get(currentControlOverlay.value, 'current') && currentControlOverlay.value.prefix == 'shapeselect-') {
        setShapeSelectOverlayVisibility(null, false)
      }

      let clickedDistanceShape = null

      // Find all shapes under click point
      let clickedShapes = shapeRegistry.initedShapes.value.filter((shape) => {
        if (shape.isLatLngClickMatch(event.latLng)) {
          if (shape.getType() == 'distance') {
            clickedDistanceShape = shape
          }
          return true
        }
        return false
      }).sort((a1, a2) => {
        // Sort by size (smallest first)
        return (Math.abs(a1.getCalculated()) > Math.abs(a2.getCalculated())) ? 1 : -1
      })

      // Both area and distance clicked - show selector
      if (clickedShapes.length > 1 && clickedDistanceShape) {
        mainStore.setCurrentShape(null)
        return setShapeSelectOverlayVisibility(event, true, {
          area: clickedShapes.find(shape => shape.getType() == 'area'),
          distance: clickedShapes.find(shape => shape.getType() == 'distance')
        })
      }

      // Single shape clicked - select it
      mainStore.setLastMapClickEvent({ event, delayedClear: true })
      mainStore.setCurrentShape(clickedShapes[0])
    }
  }

  /**
   * Watch mode changes and update UI accordingly
   */
  watch(mode, (newMode) => {
    setCursor()
    shapeRegistry.initedShapes.value.forEach(shape => shape.setGlobalMode(newMode))

    if (newMode == 'free') {
      buefy.snackbar.open({
        ...snackbarDefaults,
        queue: false,
        message: `Click or tap anywhere to start drawing.`
      })

      shapeRegistry.initedShapes.value.forEach(shape => shape.setMode('free'))
      mainStore.setCurrentShape(null)
      return
    }

    if (currentInitedShape.value) {
      currentInitedShape.value.setMode(newMode)
    }
  }, { immediate: true })

  /**
   * Watch cursor changes and apply to map
   */
  watch(cursor, (newCursor) => {
    if (map.value) {
      map.value.setOptions({ 'draggableCursor': newCursor })
    }
  })

  return {
    shapeSelectOverlay,
    cursor,
    setCursor,
    setShapeSelectOverlayVisibility,
    handleMapClick
  }
}
